import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper
} from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import { db } from "../firebase";
import { tryResolveTenantId } from '../utils/tenant';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { useTheme, alpha } from "@mui/material/styles";

export default function DepartmentsManager({
  selectedLocationId,
  selectedDepartmentId,
  onSelectDepartment,
  onDeleteDepartment,
  embedded = false,
  locationName = "",
}) {
  const theme = useTheme();
  const [departments, setDepartments] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [editingDept, setEditingDept] = useState(null);
  const [editingOpen, setEditingOpen] = useState(false);
  const [editingName, setEditingName] = useState("");

  useEffect(() => {

    if (!selectedLocationId) {
      setDepartments([]);
      onSelectDepartment(null);
      return;
    }
    let unsub = null;
    (async () => {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) return;
      const q = query(
        collection(db, `tenants/${tenantId}/departments`),
        where("locationId", "==", selectedLocationId),
        orderBy("name")
      );
      unsub = onSnapshot(q, snap => {
        const deptList = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(d => !d.archived);

        setDepartments(deptList);
        // Always auto-select the first department if none is selected or selected is missing
        if (deptList.length) {
          if (!selectedDepartmentId || !deptList.some(d => d.id === selectedDepartmentId)) {
            onSelectDepartment(deptList[0].id);
          }
        } else {
          onSelectDepartment(null);
        }
      });
    })();
    return () => { if (unsub) unsub(); };
  }, [selectedLocationId, selectedDepartmentId, onSelectDepartment]);

  const handleAdd = async () => {
    if (!newDept.trim() || !selectedLocationId) return;
    const tenantId = await tryResolveTenantId();
    if (!tenantId) return;
    const docRef = await addDoc(collection(db, `tenants/${tenantId}/departments`), {
      name: newDept.trim(),
      locationId: selectedLocationId,
    });
    setNewDept("");
    if (docRef && docRef.id) {
      onSelectDepartment(docRef.id);
    }
  };

  const handleDelete = async (id) => {
    const tenantId = await tryResolveTenantId();
    if (!tenantId) return;
  await updateDoc(doc(db, `tenants/${tenantId}/departments`, id), { archived: true, updatedAt: new Date() });
  };

  if (!selectedLocationId) return (
    <Box
      component={embedded ? Box : Paper}
      sx={{
        p: embedded ? 0 : 3,
        bgcolor: embedded ? 'transparent' : theme.palette.background.paper
      }}
    >
      <Typography variant="h6" color="primary.main">Departments</Typography>
      <Typography color="text.secondary" sx={{ mt: 2 }}>Select a location to manage departments.</Typography>
    </Box>
  );

  return (
    <Box
      component={embedded ? Box : Paper}
      sx={{
        p: embedded ? 0 : { xs: 2, sm: 4 },
        mb: embedded ? 0 : 4,
        borderRadius: embedded ? 0 : 4,
        boxShadow: embedded ? 'none' : 3,
        bgcolor: embedded ? 'transparent' : theme.palette.background.paper
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant={embedded ? "h6" : "h5"}
          fontWeight={700}
          gutterBottom
          color="primary.main"
        >
          Departments
        </Typography>
        {embedded && (
          <Typography variant="body2" color="text.secondary">
            Departments inside {locationName || "the selected location"}.
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 2, mt: 2, alignItems: "flex-end", flexWrap: 'wrap' }}>
        <TextField
          label="New Department"
          value={newDept}
          onChange={e => setNewDept(e.target.value)}
          fullWidth
          size="small"
          sx={{ maxWidth: 300, input: { color: theme.palette.text.primary } }}
        />
        <Button variant="contained" onClick={handleAdd} sx={{ height: 40 }}>
          Add
        </Button>
      </Box>
      <Table
        sx={{
          mt: 3,
          borderRadius: 2,
          overflow: 'hidden',
          background: embedded ? 'transparent' : alpha(theme.palette.background.paper, 0.9)
        }}
      >
        <TableHead>
          <TableRow sx={{ background: alpha(theme.palette.background.paper, 0.8) }}>
            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Name</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {departments.map(d => (
            <TableRow
              key={d.id}
              selected={d.id === selectedDepartmentId}
              onClick={() => onSelectDepartment(d.id)}
              hover
              sx={{ cursor: "pointer", transition: 'background 0.2s' }}
            >
              <TableCell>
                <strong style={{ color: d.id === selectedDepartmentId ? theme.palette.primary.main : theme.palette.text.primary }}>
                  {d.name}
                </strong>
              </TableCell>
              <TableCell align="right">
                <IconButton color="primary" onClick={e => { e.stopPropagation(); setEditingDept(d); setEditingName(d.name || ''); setEditingOpen(true); }}>
                  <EditIcon />
                </IconButton>
                <IconButton color="error" onClick={e => { e.stopPropagation();
                  if (typeof onDeleteDepartment === 'function') { onDeleteDepartment(d); return; }
                  handleDelete(d.id);
                }}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Edit Department Dialog */}
      <Dialog open={editingOpen} onClose={() => setEditingOpen(false)}>
        <DialogTitle>Edit department</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={editingName}
            onChange={e => setEditingName(e.target.value)}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            if (!editingDept || !editingName.trim()) return;
            const tenantId = await tryResolveTenantId();
            if (!tenantId) return;
            await updateDoc(doc(db, `tenants/${tenantId}/departments`, editingDept.id), { name: editingName.trim() });
            setEditingOpen(false);
            setEditingDept(null);
            setEditingName('');
          }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
